/**
 * PSYKHE tracking for the Nosto-rendered PLP.
 *
 * Emits the two mandatory PSYKHE events (`psykhe_list_view`,
 * `psykhe_list_click`) via Shopify's analytics pixel API. Runs against
 * Nosto's DOM (`#nosto-collection-target`) so it can ship independently
 * of the webpack-built theme code.
 *
 * Drop this file into the theme's `assets/` directory and load it on
 * collection templates, e.g. in theme.liquid or a snippet:
 *
 *   {% if template.name == 'collection' %}
 *     <script src="{{ 'psykhe-nosto-tracking.js' | asset_url }}" defer></script>
 *   {% endif %}
 *
 * Notes:
 * - `recommendationId` is intentionally omitted from all events since
 *   Nosto is not PSYKHE — no recommendation ID exists yet. PSYKHE has
 *   confirmed this is expected for the pre-bucketing rollout.
 * - No side effects on Nosto's rendering; the script only reads the DOM
 *   and attaches passive listeners.
 * - Append `?plp_debug=1` to any collection URL to log fired events.
 */
(function () {
  'use strict';

  // ---------- config ----------
  var NOSTO_TARGET_SELECTOR = '#nosto-collection-target';
  // Selectors used to identify a "product card" inside the Nosto grid.
  // Add / remove entries to match your actual card markup.
  var CARD_SELECTORS = [
    'article[data-product-id]',
    'article.nosto-product-card',
    '.nosto-product-card',
    '[data-product-card]',
    '#nosto-collection-target article',
  ];
  // Selectors for a PDP link inside a card. First match wins.
  var LINK_SELECTORS = [
    'a[href*="/products/"]',
  ];
  var CLICK_DEDUPE_MS = 200;
  var DEVICE_ID_KEY = 'tnk_psykhe_did';

  // ---------- helpers ----------
  function isDebug() {
    try {
      return new URLSearchParams(window.location.search).has('plp_debug');
    } catch (e) {
      return false;
    }
  }
  function log() {
    if (!isDebug()) return;
    try {
      // eslint-disable-next-line no-console
      console.log.apply(console, ['[psykhe-nosto]'].concat([].slice.call(arguments)));
    } catch (e) { /* noop */ }
  }

  function getDeviceId() {
    try {
      var id = window.localStorage.getItem(DEVICE_ID_KEY);
      if (id) return id;
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        id = window.crypto.randomUUID();
      } else {
        id = 'did-' + Date.now() + '-' + Math.random().toString(36).slice(2);
      }
      window.localStorage.setItem(DEVICE_ID_KEY, id);
      return id;
    } catch (e) {
      return 'did-' + Date.now();
    }
  }
  function getUserId() {
    try {
      // Reuse the same signal as our React grid does.
      return (window.TMW && window.TMW.customer && window.TMW.customer.id) || null;
    } catch (e) {
      return null;
    }
  }
  function getCurrencyCode() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) { /* noop */ }
    return 'USD';
  }
  function getCollectionHandle() {
    // /collections/<handle> or /collections/<handle>/<something>
    var m = window.location.pathname.match(/\/collections\/([^/?#]+)/i);
    return m ? m[1] : null;
  }

  // ---------- Shopify pixel emit ----------
  function publishPsykhe(eventName, payload) {
    log(eventName, payload);
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) { /* tracking is non-critical */ }
  }

  // ---------- product extraction ----------
  function findCards(root) {
    if (!root) return [];
    for (var i = 0; i < CARD_SELECTORS.length; i += 1) {
      var found = root.querySelectorAll(CARD_SELECTORS[i]);
      if (found && found.length) return [].slice.call(found);
    }
    return [];
  }

  function readProductId(card) {
    if (!card) return null;
    // Try common data attributes first.
    var attrs = ['data-product-id', 'data-productid', 'data-plp-product-id'];
    for (var i = 0; i < attrs.length; i += 1) {
      var val = card.getAttribute(attrs[i]);
      if (val) return String(val).split('/').pop();
    }
    // Fall back to a nested element carrying the id (rivo favorite btn etc.)
    var nested = card.querySelector('[data-product-id]');
    if (nested) return String(nested.getAttribute('data-product-id')).split('/').pop();
    // Last resort: the PDP link's handle. Not a numeric id, but PSYKHE
    // accepts strings — better than nothing.
    var link = card.querySelector(LINK_SELECTORS[0]);
    if (link && link.getAttribute('href')) {
      var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (m) return m[1];
    }
    return null;
  }
  function readVariantId(card) {
    if (!card) return undefined;
    var el = card.querySelector('[data-variant-id]');
    if (el) return String(el.getAttribute('data-variant-id')).split('/').pop();
    return undefined;
  }
  function readTitle(card) {
    if (!card) return '';
    // Prefer the PDP link's text or a heading.
    var link = card.querySelector(LINK_SELECTORS[0]);
    var heading = card.querySelector('h1, h2, h3, h4, h5, h6');
    var candidates = [heading, link];
    for (var i = 0; i < candidates.length; i += 1) {
      var el = candidates[i];
      if (el && el.textContent) {
        var text = el.textContent.trim();
        if (text) return text;
      }
    }
    return '';
  }
  function readPrice(card) {
    if (!card) return 0;
    // Look for text containing a currency symbol + digits. Skip obvious
    // strike-through (compare-at) so we favour the current price.
    var priceEls = card.querySelectorAll('.price, [class*="price" i]');
    var texts = [];
    for (var i = 0; i < priceEls.length; i += 1) {
      if (priceEls[i].tagName === 'DEL') continue;
      texts.push(priceEls[i].textContent || '');
    }
    if (texts.length === 0) {
      // Fall back: scan the whole card.
      texts.push(card.textContent || '');
    }
    for (var j = 0; j < texts.length; j += 1) {
      var m = texts[j].replace(/,/g, '').match(/(?:\$|£|€)\s*(\d+(?:\.\d+)?)/);
      if (m) return parseFloat(m[1]);
    }
    return 0;
  }
  function toTrackingProduct(card, position) {
    var pid = readProductId(card);
    if (!pid) return null;
    return {
      productIdentifier: pid,
      variantIdentifier: readVariantId(card),
      name: readTitle(card),
      price: readPrice(card),
      position: position,
      currency: getCurrencyCode(),
    };
  }

  // ---------- list_view (batch dedupe) ----------
  var seenProductIds = Object.create(null);
  var positionCounter = 0;

  function collectNewBatch(root) {
    var cards = findCards(root);
    var batch = [];
    for (var i = 0; i < cards.length; i += 1) {
      var pid = readProductId(cards[i]);
      if (!pid || seenProductIds[pid]) continue;
      seenProductIds[pid] = true;
      positionCounter += 1;
      var product = toTrackingProduct(cards[i], positionCounter);
      if (product) batch.push(product);
    }
    return batch;
  }

  function emitViewFor(root) {
    var productList = getCollectionHandle();
    if (!productList) return;
    var batch = collectNewBatch(root);
    if (!batch.length) return;
    publishPsykhe('psykhe_list_view', {
      productList: productList,
      // recommendationId intentionally omitted — Nosto is not PSYKHE.
      products: batch,
    });
  }

  // ---------- list_click (click, contextmenu, auxclick) ----------
  var lastFireAt = 0;
  var lastFireId = null;
  function handleActivation(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    // Only track PDP link activations, not swatches / quick-add buttons.
    var link = target.closest('a[href*="/products/"]');
    if (!link) return;
    // Must be inside a Nosto grid card.
    var card = null;
    for (var i = 0; i < CARD_SELECTORS.length; i += 1) {
      card = link.closest(CARD_SELECTORS[i]);
      if (card) break;
    }
    if (!card) return;
    var pid = readProductId(card);
    if (!pid) return;
    // De-dupe: browsers may fire both click and auxclick for one action.
    var now = Date.now();
    if (pid === lastFireId && now - lastFireAt < CLICK_DEDUPE_MS) return;
    lastFireId = pid;
    lastFireAt = now;

    // Position is the card's rank in the currently rendered grid.
    var root = document.querySelector(NOSTO_TARGET_SELECTOR);
    var cards = findCards(root);
    var position = 0;
    for (var j = 0; j < cards.length; j += 1) {
      if (readProductId(cards[j]) === pid) { position = j + 1; break; }
    }

    var product = toTrackingProduct(card, position);
    if (!product) return;
    publishPsykhe('psykhe_list_click', {
      productList: getCollectionHandle(),
      // recommendationId intentionally omitted — Nosto is not PSYKHE.
      product: product,
    });
  }

  // ---------- init / lifecycle ----------
  function initGridWatcher() {
    var root = document.querySelector(NOSTO_TARGET_SELECTOR);
    if (!root) {
      // Grid may mount after this script — try again shortly.
      window.setTimeout(initGridWatcher, 500);
      return;
    }
    log('watching', root);
    // Prime device id so it exists for later joins in analytics.
    getDeviceId();
    // Initial scan (in case Nosto's SDK already painted).
    emitViewFor(root);
    // React to Nosto's subsequent renders / infinite scroll appends.
    try {
      var observer = new MutationObserver(function () {
        emitViewFor(root);
      });
      observer.observe(root, { childList: true, subtree: true });
    } catch (e) {
      // MutationObserver unavailable — fall back to a coarse interval.
      window.setInterval(function () { emitViewFor(root); }, 1500);
    }
    // Nosto or the theme may dispatch a rendered event too.
    window.addEventListener('productCardsRendered', function () {
      emitViewFor(root);
    });
  }

  function init() {
    // Only run on collection pages.
    if (!getCollectionHandle()) return;
    document.addEventListener('click', handleActivation, true);
    document.addEventListener('contextmenu', handleActivation, true);
    document.addEventListener('auxclick', handleActivation, true);
    initGridWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
